<script setup>
import { onBeforeUnmount, reactive, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { TextSelection } from '@tiptap/pm/state'
import { paragraphAlign, PARAGRAPH_ALIGNMENTS, RUN_COLORS, RUN_HIGHLIGHT_COLORS, RUN_HIGHLIGHTS, runColor, runHighlight } from './ooxmlCss.js'

// RED-317 PR 3b — the Studio's formatting toolbar (prototype `RichEditor`):
// B/I/U/S, text colour and highlight (contract §1 palettes), alignment
// (§2), "Alapealkiri", bullet and numbered lists with indent/outdent (§3), a
// new table with a header row (§4), a picture (§5), undo/redo. Active states
// are copied into one reactive object per transaction instead of calling
// isActive() per button per render on a large document.
const props = defineProps({
  editor: { type: Object, default: null },
  // The heading level "Alapealkiri" makes: one below the section's own heading (§4.x is a Heading 2).
  subheadingLevel: { type: Number, default: 3 },
  uploading: { type: Boolean, default: false },
})

const { t } = useI18n()

const state = reactive({
  bold: false,
  italic: false,
  underline: false,
  strike: false,
  color: null,
  highlight: null,
  align: null,
  subheading: false,
  canSubheading: false,
  bulletList: false,
  orderedList: false,
  canIndent: false,
  canOutdent: false,
  inTable: false,
  canUndo: false,
  canRedo: false,
})

let unbind = null

watch(
  () => props.editor,
  (instance) => {
    unbind?.()
    unbind = null
    if (!instance) return

    const sync = () => {
      const run = instance.getAttributes('ooxmlRun')
      const block = instance.state.selection.$from.parent
      state.bold = instance.isActive('bold')
      state.italic = instance.isActive('italic')
      state.underline = instance.isActive('underline')
      state.strike = instance.isActive('strike')
      state.color = runColor(run)
      state.highlight = runHighlight(run)
      state.align = block.isTextblock ? paragraphAlign(block.attrs) : null
      state.subheading = instance.isActive('heading', { level: props.subheadingLevel })
      state.canSubheading = instance.can().toggleSubheading(props.subheadingLevel)
      state.bulletList = instance.isActive('bulletList')
      state.orderedList = instance.isActive('orderedList')
      state.canIndent = instance.can().sinkListItem('listItem')
      state.canOutdent = instance.can().liftListItem('listItem')
      state.inTable = instance.isActive('table')
      state.canUndo = instance.can().undo()
      state.canRedo = instance.can().redo()
    }
    instance.on('transaction', sync)
    instance.on('selectionUpdate', sync)
    sync()
    unbind = () => {
      instance.off('transaction', sync)
      instance.off('selectionUpdate', sync)
    }
  },
  { immediate: true },
)

onBeforeUnmount(() => unbind?.())

const chain = () => props.editor.chain().focus()

const colorPopover = useTemplateRef('colorPopover')
const highlightPopover = useTemplateRef('highlightPopover')
const fileInput = useTemplateRef('fileInput')

const setColor = (color) => {
  chain().setRunColor(color).run()
  colorPopover.value?.hide()
}

const setHighlight = (name) => {
  chain().setRunHighlight(name).run()
  highlightPopover.value?.hide()
}

/** "Alapealkiri" toggles; Word's own heading levels are not offered. */
const toggleSubheading = () => chain().toggleSubheading(props.subheadingLevel).run()

/**
 * A new table (tblPr/tblGrid null: the compiler gives it the fragment's grid
 * style or single borders, contract §4) with a header row, never replacing
 * what is selected: a range collapses to its end first. Like Word, a table
 * that ends the page (or a cell) gets an empty paragraph after it, so there
 * is somewhere to click and type below it.
 */
const insertTable = () => {
  chain()
    .command(({ tr }) => {
      if (!(tr.selection instanceof TextSelection) || !tr.selection.empty) {
        tr.setSelection(TextSelection.near(tr.doc.resolve(tr.selection.to)))
      }
      return true
    })
    .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
    .command(({ tr }) => {
      const { $head } = tr.selection
      for (let depth = $head.depth; depth > 0; depth--) {
        if ($head.node(depth).type.spec.tableRole === 'table') {
          if ($head.after(depth) === $head.end(depth - 1)) {
            tr.insert($head.after(depth), tr.doc.type.schema.nodes.paragraph.create())
          }
          break
        }
      }
      return true
    })
    .run()
}

const onPickPictures = (event) => {
  const files = [...(event.target.files ?? [])]
  event.target.value = ''
  if (files.length) {
    props.editor.chain().focus().insertPictureFiles(files).run()
  }
}

const swatch = (color) => (color === 'auto' ? '#000000' : `#${color}`)

const alignIcons = { left: 'pi-align-left', center: 'pi-align-center', right: 'pi-align-right', justify: 'pi-align-justify' }

const buttonClass = (active = false) => [
  'inline-flex h-7 min-w-7 cursor-pointer items-center justify-center gap-1 rounded-md border-none px-1 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-35',
  active ? 'bg-white/20 text-white' : 'bg-transparent text-slate-200 hover:bg-white/10 hover:text-white',
]
</script>

<template>
  <div
    v-if="editor"
    class="flex flex-wrap items-center justify-center gap-px rounded-xl bg-slate-900 p-1 shadow-lg ring-1 ring-black/20"
    role="toolbar"
    :aria-label="t('messages.editor.toolbar')"
    data-testid="studio-toolbar"
  >
    <button v-tooltip.bottom="t('messages.editor.bold')" type="button" :class="[buttonClass(state.bold), 'font-bold']" :aria-label="t('messages.editor.bold')" :aria-pressed="state.bold" data-testid="studio-tool-bold" @click="chain().toggleBold().run()">B</button>
    <button v-tooltip.bottom="t('messages.editor.italic')" type="button" :class="[buttonClass(state.italic), 'font-serif italic']" :aria-label="t('messages.editor.italic')" :aria-pressed="state.italic" data-testid="studio-tool-italic" @click="chain().toggleItalic().run()">I</button>
    <button v-tooltip.bottom="t('messages.editor.underline')" type="button" :class="[buttonClass(state.underline), 'underline']" :aria-label="t('messages.editor.underline')" :aria-pressed="state.underline" data-testid="studio-tool-underline" @click="chain().toggleUnderline().run()">U</button>
    <button v-tooltip.bottom="t('messages.editor.strike')" type="button" :class="[buttonClass(state.strike), 'line-through']" :aria-label="t('messages.editor.strike')" :aria-pressed="state.strike" data-testid="studio-tool-strike" @click="chain().toggleStrike().run()">S</button>

    <span class="mx-0.5 h-5 w-px bg-white/20" aria-hidden="true" />

    <button v-tooltip.bottom="t('messages.editor.textColor')" type="button" :class="buttonClass()" :aria-label="t('messages.editor.textColor')" aria-haspopup="true" data-testid="studio-tool-color" @click="colorPopover.toggle($event)">
      <span class="flex flex-col items-center leading-none">
        <span class="text-sm font-semibold">A</span>
        <span class="mt-0.5 h-1 w-4 rounded-sm" :style="{ backgroundColor: state.color ? swatch(state.color) : 'transparent' }" :class="{ 'ring-1 ring-white/40': !state.color }" />
      </span>
    </button>
    <Popover ref="colorPopover" data-testid="studio-color-popover">
      <div class="flex items-center gap-1.5">
        <button
          v-for="color in RUN_COLORS"
          :key="color"
          v-tooltip.bottom="t(`messages.editor.colors.${color}`)"
          type="button"
          class="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border border-gray-300"
          :class="{ 'ring-2 ring-primary-500 ring-offset-1': state.color === color }"
          :style="{ backgroundColor: color === 'auto' ? '#ffffff' : swatch(color) }"
          :aria-label="t(`messages.editor.colors.${color}`)"
          :aria-pressed="state.color === color"
          :data-testid="`studio-color-${color}`"
          @click="setColor(color)"
        >
          <span v-if="color === 'auto'" class="text-xs font-semibold text-gray-900">A</span>
        </button>
      </div>
    </Popover>

    <button v-tooltip.bottom="t('messages.editor.highlight')" type="button" :class="buttonClass()" :aria-label="t('messages.editor.highlight')" aria-haspopup="true" data-testid="studio-tool-highlight" @click="highlightPopover.toggle($event)">
      <span class="flex flex-col items-center leading-none">
        <svg width="14" height="12" viewBox="0 0 16 14" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round" aria-hidden="true">
          <path d="M3 10.5 9.5 4l2.5 2.5L5.5 13H3v-2.5Z" />
          <path d="m8.5 5 2.5 2.5" />
        </svg>
        <span class="mt-0.5 h-1 w-4 rounded-sm" :style="{ backgroundColor: state.highlight && state.highlight !== 'none' ? RUN_HIGHLIGHT_COLORS[state.highlight] : 'transparent' }" :class="{ 'ring-1 ring-white/40': !state.highlight || state.highlight === 'none' }" />
      </span>
    </button>
    <Popover ref="highlightPopover" data-testid="studio-highlight-popover">
      <div class="flex flex-col gap-2">
        <div class="grid grid-cols-8 gap-1.5">
          <button
            v-for="name in RUN_HIGHLIGHTS"
            :key="name"
            v-tooltip.bottom="t(`messages.editor.highlights.${name}`)"
            type="button"
            class="h-6 w-6 cursor-pointer rounded-sm border border-gray-300"
            :class="{ 'ring-2 ring-primary-500 ring-offset-1': state.highlight === name }"
            :style="{ backgroundColor: RUN_HIGHLIGHT_COLORS[name] }"
            :aria-label="t(`messages.editor.highlights.${name}`)"
            :aria-pressed="state.highlight === name"
            :data-testid="`studio-highlight-${name}`"
            @click="setHighlight(name)"
          />
        </div>
        <Button type="button" size="small" text severity="secondary" icon="pi pi-eraser" class="w-auto! self-start" :label="t('messages.editor.highlightNone')" data-testid="studio-highlight-none" @click="setHighlight('none')" />
      </div>
    </Popover>

    <span class="mx-0.5 h-5 w-px bg-white/20" aria-hidden="true" />

    <button
      v-for="align in PARAGRAPH_ALIGNMENTS"
      :key="align"
      v-tooltip.bottom="t(`messages.editor.align.${align}`)"
      type="button"
      :class="buttonClass(state.align === align)"
      :aria-label="t(`messages.editor.align.${align}`)"
      :aria-pressed="state.align === align"
      :data-testid="`studio-tool-align-${align}`"
      @click="chain().setParagraphAlign(align).run()"
    >
      <span class="pi text-sm" :class="alignIcons[align]" />
    </button>

    <span class="mx-0.5 h-5 w-px bg-white/20" aria-hidden="true" />

    <button
      v-tooltip.bottom="state.canSubheading || state.subheading ? t('messages.editor.subheadingHint') : t('messages.editor.subheadingUnavailable')"
      type="button"
      :class="[buttonClass(state.subheading), 'px-2 text-xs font-semibold']"
      :disabled="!state.canSubheading"
      :aria-pressed="state.subheading"
      data-testid="studio-tool-subheading"
      @click="toggleSubheading"
    >
      {{ t('messages.editor.subheading') }}
    </button>
    <button v-tooltip.bottom="t('messages.editor.bulletList')" type="button" :class="buttonClass(state.bulletList)" :aria-label="t('messages.editor.bulletList')" :aria-pressed="state.bulletList" data-testid="studio-tool-bullet-list" @click="chain().toggleBulletList().run()">
      <span class="pi pi-list text-sm" />
    </button>
    <button v-tooltip.bottom="t('messages.editor.orderedList')" type="button" :class="[buttonClass(state.orderedList), 'text-xs font-semibold']" :aria-label="t('messages.editor.orderedList')" :aria-pressed="state.orderedList" data-testid="studio-tool-ordered-list" @click="chain().toggleOrderedList().run()">1.</button>
    <button v-tooltip.bottom="t('messages.editor.outdent')" type="button" :class="buttonClass()" :disabled="!state.canOutdent" :aria-label="t('messages.editor.outdent')" data-testid="studio-tool-outdent" @click="chain().liftListItem('listItem').run()">
      <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" aria-hidden="true">
        <path d="M7 3.5h7M7 8h7M7 12.5h7M4.5 5.5 2 8l2.5 2.5" />
      </svg>
    </button>
    <button v-tooltip.bottom="t('messages.editor.indent')" type="button" :class="buttonClass()" :disabled="!state.canIndent" :aria-label="t('messages.editor.indent')" data-testid="studio-tool-indent" @click="chain().sinkListItem('listItem').run()">
      <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" aria-hidden="true">
        <path d="M7 3.5h7M7 8h7M7 12.5h7M2 5.5 4.5 8 2 10.5" />
      </svg>
    </button>

    <span class="mx-0.5 h-5 w-px bg-white/20" aria-hidden="true" />

    <button v-tooltip.bottom="t('messages.editor.insertTable')" type="button" :class="buttonClass()" :disabled="state.inTable" :aria-label="t('messages.editor.insertTable')" data-testid="studio-tool-table" @click="insertTable">
      <span class="pi pi-table text-sm" />
    </button>
    <button v-tooltip.bottom="t('messages.editor.insertImage')" type="button" :class="buttonClass()" :disabled="uploading" :aria-label="t('messages.editor.insertImage')" data-testid="studio-tool-image" @click="fileInput.click()">
      <span class="pi text-sm" :class="uploading ? 'pi-spinner pi-spin' : 'pi-image'" />
    </button>
    <input ref="fileInput" type="file" class="hidden" accept="image/png,image/jpeg,image/gif" multiple data-testid="studio-image-input" @change="onPickPictures" />

    <span class="mx-0.5 h-5 w-px bg-white/20" aria-hidden="true" />

    <button v-tooltip.bottom="t('messages.editor.undo')" type="button" :class="buttonClass()" :disabled="!state.canUndo" :aria-label="t('messages.editor.undo')" data-testid="studio-tool-undo" @click="chain().undo().run()">
      <span class="pi pi-undo text-sm" />
    </button>
    <button v-tooltip.bottom="t('messages.editor.redo')" type="button" :class="buttonClass()" :disabled="!state.canRedo" :aria-label="t('messages.editor.redo')" data-testid="studio-tool-redo" @click="chain().redo().run()">
      <!-- PrimeIcons has no redo glyph: the undo arrow, mirrored. -->
      <span class="pi pi-undo -scale-x-100 text-sm" />
    </button>
  </div>
</template>
